import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Responsive helpers
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
    };
  }, [breakpoint]);
  return isMobile;
};

const styles: { [k: string]: React.CSSProperties } = {
  container: { fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial", color: "#0f172a", lineHeight: 1.7, backgroundColor: "#f8fafc" },
  hero: { padding: "56px 32px", background: "linear-gradient(135deg, #0ea5a4 0%, #0d9488 100%)", color: "#fff" },
  heroInner: { maxWidth: 1200, margin: "0 auto" },
  heroTitle: { fontSize: 40, fontWeight: 700, margin: 0 },
  heroSubtitle: { fontSize: 18, marginTop: 12, opacity: 0.95 },
  section: { padding: "48px 32px", background: "#fff", marginBottom: 2 },
  sectionInner: { maxWidth: 1100, margin: "0 auto" },
  sectionTitle: { fontSize: 28, fontWeight: 700, marginBottom: 24, color: "#0f172a" },
  subsectionTitle: { fontSize: 20, fontWeight: 600, marginTop: 32, marginBottom: 16, color: "#1e293b" },
  paragraph: { marginBottom: 16, color: "#475569", fontSize: 15 },
  list: { marginLeft: 24, marginBottom: 16, color: "#475569" },
  listItem: { marginBottom: 10 },
  highlight: { background: "#f1f5f9", padding: "20px 24px", borderRadius: 10, marginBottom: 20, borderLeft: "4px solid #0ea5a4" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginTop: 24 },
  card: { padding: 24, borderRadius: 12, background: "#f8fafc", border: "1px solid #e2e8f0" },
  cardTitle: { fontSize: 18, fontWeight: 600, marginBottom: 12, color: "#0f172a" },
  cardText: { fontSize: 14, color: "#64748b", lineHeight: 1.6 },
  footer: { padding: "32px 32px", borderTop: "1px solid #e6edf3", marginTop: 24, fontSize: 14, color: "#64748b", textAlign: "center", background: "#fff" },
  backButton: { padding: "10px 20px", borderRadius: 8, background: "#fff", color: "#0ea5a4", border: "1px solid #0ea5a4", cursor: "pointer", fontSize: 15, fontWeight: 500, marginTop: 20 },
};

const PrivacyPolicy: React.FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <section style={{ ...styles.hero, padding: isMobile ? "40px 20px" : styles.hero.padding }}>
        <div style={styles.heroInner}>
          <h1 style={{ ...styles.heroTitle, fontSize: isMobile ? 32 : styles.heroTitle.fontSize }}>隱私權政策</h1>
          <p style={{ ...styles.heroSubtitle, fontSize: isMobile ? 16 : styles.heroSubtitle.fontSize }}>
            好朋友租賃重視您的隱私權，本政策說明我們如何收集、使用和保護您的個人資訊
          </p>
          <p style={{ fontSize: 14, marginTop: 16, opacity: 0.9 }}>最後更新日期：2025年11月24日</p>
        </div>
      </section>

      {/* Introduction */}
      <section style={{ ...styles.section, padding: isMobile ? "32px 20px" : styles.section.padding }}>
        <div style={styles.sectionInner}>
          <h2 style={{ ...styles.sectionTitle, fontSize: isMobile ? 24 : styles.sectionTitle.fontSize }}>政策概述</h2>
          <p style={styles.paragraph}>
            歡迎使用好朋友租賃服務。我們深知個人資訊的重要性，並承諾保護您的隱私。本隱私權政策旨在幫助您了解我們如何收集、使用、分享和保護您在使用我們的服務時所提供的個人資訊。
          </p>
          <p style={styles.paragraph}>
            當您使用我們的服務時，即表示您同意本隱私權政策的內容。如您不同意本政策的任何部分，請勿使用我們的服務。
          </p>
        </div>
      </section>

      {/* Information Collection */}
      <section style={{ ...styles.section, padding: isMobile ? "32px 20px" : styles.section.padding }}>
        <div style={styles.sectionInner}>
          <h2 style={{ ...styles.sectionTitle, fontSize: isMobile ? 24 : styles.sectionTitle.fontSize }}>資訊收集</h2>

          <h3 style={styles.subsectionTitle}>我們收集的資訊類型</h3>
          <p style={styles.paragraph}>為了提供優質的服務，我們可能會收集以下類型的資訊：</p>

          <div style={styles.grid}>
            <div style={styles.card}>
              <h4 style={styles.cardTitle}>📋 基本個人資訊</h4>
              <p style={styles.cardText}>
                姓名、電話號碼、電子郵件地址、身分證字號（僅司機申請時）
              </p>
            </div>
            <div style={styles.card}>
              <h4 style={styles.cardTitle}>🚗 訂單相關資訊</h4>
              <p style={styles.cardText}>
                上下車地址、航班資訊、乘車時間、乘客人數、行李數量、特殊需求
              </p>
            </div>
            <div style={styles.card}>
              <h4 style={styles.cardTitle}>💳 付款資訊</h4>
              <p style={styles.cardText}>
                付款方式、交易記錄（透過第三方支付平台處理，我們不儲存完整信用卡資訊）
              </p>
            </div>
            <div style={styles.card}>
              <h4 style={styles.cardTitle}>📱 裝置與使用資訊</h4>
              <p style={styles.cardText}>
                裝置類型、IP位址、瀏覽器類型、使用時間、頁面瀏覽記錄
              </p>
            </div>
          </div>

          <h3 style={styles.subsectionTitle}>資訊收集方式</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>您直接向我們提供的資訊（如註冊帳戶、預訂服務時）</li>
            <li style={styles.listItem}>自動收集的資訊（如透過 Cookie 和類似技術）</li>
            <li style={styles.listItem}>來自第三方的資訊（如社交媒體登入、地圖服務提供商）</li>
          </ul>
        </div>
      </section>

      {/* Information Usage */}
      <section style={{ ...styles.section, padding: isMobile ? "32px 20px" : styles.section.padding }}>
        <div style={styles.sectionInner}>
          <h2 style={{ ...styles.sectionTitle, fontSize: isMobile ? 24 : styles.sectionTitle.fontSize }}>資訊使用</h2>

          <p style={styles.paragraph}>我們使用收集的資訊用於以下目的：</p>

          <div style={styles.highlight}>
            <strong style={{ fontSize: 16, color: "#0f172a" }}>主要用途：</strong>
            <ul style={{ ...styles.list, marginTop: 12, marginBottom: 0 }}>
              <li style={styles.listItem}>提供、維護和改善我們的服務</li>
              <li style={styles.listItem}>處理您的訂單和付款</li>
              <li style={styles.listItem}>與您溝通服務相關事項</li>
              <li style={styles.listItem}>提供客戶支援</li>
              <li style={styles.listItem}>發送重要通知和更新</li>
            </ul>
          </div>

          <h3 style={styles.subsectionTitle}>其他用途</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>分析服務使用情況，優化用戶體驗</li>
            <li style={styles.listItem}>預防詐欺和確保平台安全</li>
            <li style={styles.listItem}>遵守法律義務和解決糾紛</li>
            <li style={styles.listItem}>進行市場研究和統計分析</li>
            <li style={styles.listItem}>發送促銷資訊（需獲得您的同意）</li>
          </ul>
        </div>
      </section>

      {/* Information Sharing */}
      <section style={{ ...styles.section, padding: isMobile ? "32px 20px" : styles.section.padding }}>
        <div style={styles.sectionInner}>
          <h2 style={{ ...styles.sectionTitle, fontSize: isMobile ? 24 : styles.sectionTitle.fontSize }}>資訊分享</h2>

          <p style={styles.paragraph}>
            我們尊重您的隱私，不會出售您的個人資訊。但在以下情況下，我們可能會分享您的資訊：
          </p>

          <h3 style={styles.subsectionTitle}>服務提供所需</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>司機：</strong>為完成訂單，我們會與指派的司機分享必要的訂單資訊（如乘客姓名、電話、上下車地址）</li>
            <li style={styles.listItem}><strong>第三方服務提供商：</strong>如支付處理商、地圖服務、雲端儲存服務等</li>
          </ul>

          <h3 style={styles.subsectionTitle}>法律要求</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>遵守法律、法規或法律程序</li>
            <li style={styles.listItem}>保護我們的權利、財產或安全</li>
            <li style={styles.listItem}>防止或調查可能的不當行為</li>
          </ul>

          <h3 style={styles.subsectionTitle}>業務轉讓</h3>
          <p style={styles.paragraph}>
            如發生合併、收購或資產出售，您的個人資訊可能會被轉移。我們會在轉移前通知您。
          </p>
        </div>
      </section>

      {/* Data Security */}
      <section style={{ ...styles.section, padding: isMobile ? "32px 20px" : styles.section.padding }}>
        <div style={styles.sectionInner}>
          <h2 style={{ ...styles.sectionTitle, fontSize: isMobile ? 24 : styles.sectionTitle.fontSize }}>資訊安全</h2>

          <p style={styles.paragraph}>
            我們採取適當的技術和組織措施來保護您的個人資訊，防止未經授權的存取、使用、洩露、修改或銷毀。
          </p>

          <div style={styles.grid}>
            <div style={styles.card}>
              <h4 style={styles.cardTitle}>🔒 加密技術</h4>
              <p style={styles.cardText}>
                使用 SSL/TLS 加密保護資料傳輸
              </p>
            </div>
            <div style={styles.card}>
              <h4 style={styles.cardTitle}>🛡️ 存取控制</h4>
              <p style={styles.cardText}>
                限制員工存取個人資訊，僅授權人員可存取
              </p>
            </div>
            <div style={styles.card}>
              <h4 style={styles.cardTitle}>🔐 安全監控</h4>
              <p style={styles.cardText}>
                定期進行安全審計和漏洞掃描
              </p>
            </div>
            <div style={styles.card}>
              <h4 style={styles.cardTitle}>💾 資料備份</h4>
              <p style={styles.cardText}>
                定期備份資料，確保資料完整性
              </p>
            </div>
          </div>

          <div style={{ ...styles.highlight, marginTop: 24 }}>
            <strong style={{ fontSize: 16, color: "#0f172a" }}>⚠️ 重要提醒：</strong>
            <p style={{ ...styles.paragraph, marginTop: 8, marginBottom: 0 }}>
              儘管我們盡力保護您的資訊，但沒有任何安全措施是絕對安全的。請您也採取適當措施保護您的帳戶資訊，如使用強密碼、不與他人分享登入資訊等。
            </p>
          </div>
        </div>
      </section>

      {/* User Rights */}
      <section style={{ ...styles.section, padding: isMobile ? "32px 20px" : styles.section.padding }}>
        <div style={styles.sectionInner}>
          <h2 style={{ ...styles.sectionTitle, fontSize: isMobile ? 24 : styles.sectionTitle.fontSize }}>您的權利</h2>

          <p style={styles.paragraph}>
            根據相關隱私法規，您對您的個人資訊擁有以下權利：
          </p>

          <ul style={styles.list}>
            <li style={styles.listItem}><strong>存取權：</strong>您有權要求查看我們持有的關於您的個人資訊</li>
            <li style={styles.listItem}><strong>更正權：</strong>您有權要求更正不準確或不完整的個人資訊</li>
            <li style={styles.listItem}><strong>刪除權：</strong>在某些情況下，您有權要求刪除您的個人資訊</li>
            <li style={styles.listItem}><strong>限制處理權：</strong>您有權要求限制我們處理您的個人資訊</li>
            <li style={styles.listItem}><strong>資料可攜權：</strong>您有權以結構化、常用和機器可讀的格式接收您的個人資訊</li>
            <li style={styles.listItem}><strong>反對權：</strong>您有權反對我們處理您的個人資訊</li>
            <li style={styles.listItem}><strong>撤回同意：</strong>如處理基於您的同意，您有權隨時撤回同意</li>
          </ul>

          <p style={styles.paragraph}>
            如需行使這些權利，請透過以下方式聯繫我們。我們將在合理時間內回應您的請求。
          </p>
        </div>
      </section>

      {/* Cookies */}
      <section style={{ ...styles.section, padding: isMobile ? "32px 20px" : styles.section.padding }}>
        <div style={styles.sectionInner}>
          <h2 style={{ ...styles.sectionTitle, fontSize: isMobile ? 24 : styles.sectionTitle.fontSize }}>Cookie 政策</h2>

          <p style={styles.paragraph}>
            我們使用 Cookie 和類似技術來改善您的使用體驗。Cookie 是儲存在您裝置上的小型文字檔案。
          </p>

          <h3 style={styles.subsectionTitle}>我們使用的 Cookie 類型</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>必要 Cookie：</strong>維持網站正常運作所需</li>
            <li style={styles.listItem}><strong>功能性 Cookie：</strong>記住您的偏好設定</li>
            <li style={styles.listItem}><strong>分析 Cookie：</strong>了解網站使用情況</li>
            <li style={styles.listItem}><strong>廣告 Cookie：</strong>提供相關的廣告內容</li>
          </ul>

          <p style={styles.paragraph}>
            您可以透過瀏覽器設定管理或刪除 Cookie。但請注意，這可能影響您使用我們服務的體驗。
          </p>
        </div>
      </section>

      {/* Children's Privacy */}
      <section style={{ ...styles.section, padding: isMobile ? "32px 20px" : styles.section.padding }}>
        <div style={styles.sectionInner}>
          <h2 style={{ ...styles.sectionTitle, fontSize: isMobile ? 24 : styles.sectionTitle.fontSize }}>兒童隱私</h2>

          <p style={styles.paragraph}>
            我們的服務不針對 18 歲以下的兒童。我們不會故意收集 18 歲以下兒童的個人資訊。如果您是家長或監護人，發現您的孩子向我們提供了個人資訊，請聯繫我們，我們將採取措施刪除該資訊。
          </p>
        </div>
      </section>

      {/* Updates */}
      <section style={{ ...styles.section, padding: isMobile ? "32px 20px" : styles.section.padding }}>
        <div style={styles.sectionInner}>
          <h2 style={{ ...styles.sectionTitle, fontSize: isMobile ? 24 : styles.sectionTitle.fontSize }}>政策更新</h2>

          <p style={styles.paragraph}>
            我們可能會不時更新本隱私權政策。更新後的政策將在本頁面上發布，並註明「最後更新日期」。重大變更時，我們會透過電子郵件或在網站上顯著位置通知您。
          </p>
          <p style={styles.paragraph}>
            建議您定期查看本政策以了解我們如何保護您的資訊。繼續使用我們的服務即表示您接受更新後的政策。
          </p>
        </div>
      </section>

      {/* Contact */}
      <section style={{ ...styles.section, padding: isMobile ? "32px 20px" : styles.section.padding }}>
        <div style={styles.sectionInner}>
          <h2 style={{ ...styles.sectionTitle, fontSize: isMobile ? 24 : styles.sectionTitle.fontSize }}>聯繫我們</h2>

          <p style={styles.paragraph}>
            如您對本隱私權政策有任何疑問、意見或要求，請透過以下方式聯繫我們：
          </p>

          <div style={styles.highlight}>
            <p style={{ margin: "8px 0", fontSize: 15 }}><strong>服務名稱：</strong>好朋友租賃</p>
            <p style={{ margin: "8px 0", fontSize: 15 }}><strong>電子郵件：</strong>gf66367788@gmail.com</p>
            <p style={{ margin: "8px 0", fontSize: 15 }}><strong>客服電話：</strong>0918266111</p>
            <p style={{ margin: "8px 0", fontSize: 15 }}><strong>營業時間：</strong>週一至週日 24小時</p>
          </div>

          <p style={styles.paragraph}>
            我們會在收到您的請求後盡快回覆，通常不超過 30 天。
          </p>

          <button
            style={styles.backButton}
            onClick={() => navigate(-1)}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#0ea5a4";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.color = "#0ea5a4";
            }}
          >
            ← 返回上一頁
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <p>© 2025 好朋友租賃 EasyRide. 版權所有</p>
        <p style={{ marginTop: 8, fontSize: 13 }}>
          本隱私權政策最後更新於 2025年11月24日
        </p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
